import { useAuthStore } from '~/stores/useAuthStore'
import { useSpeciesCache } from '~/composables/useSpeciesCache'
import { useApi } from '~/composables/useApi'
import { isStandaloneClient } from '~/utils/standalone'
import {
  isAdminPlayModeEnabled,
  isAdminShellPath,
  postAuthLandingPath,
} from '~/utils/adminShell'

let initialized = false
/** Client autonome : un profil local existe-t-il ? (sinon écran nouvelle partie / import) */
let standaloneHasProfile: boolean | null = null

// Pages accessible without authentication
const PUBLIC_PAGES = [
  '/welcome',
  '/login',
  '/guide',
  '/pokedex',
  '/leaderboard',
  '/maintenance',
  '/cooldown',
  '/banned',
  '/players',
  '/privacy',
  '/standalone',
]

// Pages restricted to admin role only
const ADMIN_ONLY_PATHS = ['/admin', '/debug', '/cosmetics-demo']

function isAdminOnlyPath(path: string): boolean {
  if (ADMIN_ONLY_PATHS.includes(path)) return true
  return path.startsWith('/admin/')
}

export default defineNuxtRouteMiddleware(async (to) => {
  // Skip on server
  if (import.meta.server) return

  const auth = useAuthStore()
  const path = to.path

  // Client autonome : pas de compte en ligne, le serveur local connecte d'office
  // l'unique profil. Sans profil → écran « nouvelle partie / importer ».
  if (isStandaloneClient()) {
    if (standaloneHasProfile === null) {
      const status = await useApi()
        .get<{ hasProfile: boolean }>('/api/standalone/status')
        .catch(() => ({ hasProfile: false }))
      standaloneHasProfile = status.hasProfile
    }
    if (!standaloneHasProfile && path !== '/standalone') return navigateTo('/standalone')
    if (path === '/welcome' || path === '/login') {
      return navigateTo(standaloneHasProfile ? '/' : '/standalone')
    }
    if (path === '/players' || path === '/leaderboard') {
      return navigateTo('/')
    }
  }

  // First load: check session + load species cache
  if (!initialized) {
    const { loadSpecies } = useSpeciesCache()
    await loadSpecies()
    const onAdminShell = isAdminShellPath(path)
    const shouldHydrateGame = !PUBLIC_PAGES.includes(path) && !onAdminShell
    await auth.checkAuth({ loadGameState: shouldHydrateGame })
    initialized = true
  }

  // Maintenance mode detected by checkAuth — redirect to /maintenance synchronously
  if (auth.maintenanceActive && path !== '/maintenance') {
    return navigateTo('/maintenance')
  }

  if (auth.cooldownActive && path !== '/cooldown') {
    return navigateTo('/cooldown')
  }

  // Pages publiques : accessibles sans compte, mais un joueur connecté doit
  // quand même hydrater l'état (sauf admin sur shell admin sans mode joueur).
  if (PUBLIC_PAGES.includes(path)) {
    // Page ban : pas d'hydratation jeu (session déjà refusée / absente).
    if (path === '/banned') return
    if (auth.isAuthenticated) {
      const skipGame =
        auth.user?.role === 'admin' && !isAdminPlayModeEnabled() && isAdminShellPath(path)
      if (!skipGame) await auth.ensureGameLoaded()
    }
    return
  }

  // Gameplay pages require authentication — redirect to welcome
  if (!auth.isAuthenticated) {
    return navigateTo('/welcome')
  }

  const isAdminUser = auth.user?.role === 'admin'
  const playMode = isAdminPlayModeEnabled()

  // Admin sans mode joueur : portail admin par défaut (pas de gameplay).
  if (isAdminUser && !playMode && !isAdminShellPath(path) && !PUBLIC_PAGES.includes(path)) {
    return navigateTo(postAuthLandingPath('admin'))
  }

  // Shell admin : session uniquement, pas de chargement gameplay lourd.
  if (isAdminShellPath(path)) {
    if (!isAdminUser) {
      return navigateTo('/')
    }
    return
  }

  // Un utilisateur connecté arrivant d'une page publique peut n'avoir
  // qu'une session validée, sans état de jeu chargé.
  await auth.ensureGameLoaded()

  // Admin-only pages — redirect non-admins to home
  if (isAdminOnlyPath(path) && !isAdminUser) {
    return navigateTo('/')
  }
})
